import { useTranslation } from 'react-i18next';
import { CompanyLogo } from './CompanyLogo';

export interface LetterheadCompany {
  name: string;
  address: {
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    postalCode: string;
  } | null;
  phone: string | null;
  licenseNumber: string | null;
  logoPath: string | null;
}

/**
 * The company at the top of a document (M2-19, Kyle's Q5 = A): the logo top
 * left, at most 56 px tall, with the name, address, phone and licence beside
 * it — stacked on a phone, side by side from `sm` up. Used by the estimate and
 * invoice pages and the customer's estimate link, so the three cannot drift.
 *
 * <b>The logo is decorative (`alt=""`).</b> The company's name is the text
 * right beside it; a described image would announce the same words twice —
 * the fleet thumbnail's reasoning (web/CONVENTIONS.md, M1-44).
 *
 * The image is `CompanyLogo`: no referrer, a fallback when it fails to load,
 * and a 56 px frame held before it arrives, so a phone's stacked layout does
 * not jump when the logo lands.
 */
export function Letterhead({ company }: { company: LetterheadCompany }) {
  const { t } = useTranslation();
  const address = company.address;

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4" data-testid="letterhead">
      <CompanyLogo
        path={company.logoPath}
        alt=""
        frameClassName="flex h-14 shrink-0 items-start"
        className="h-auto max-h-14 w-auto max-w-[12rem] object-contain"
        testId="company-logo"
      />
      <div className="min-w-0 text-sm">
        <p className="text-base font-semibold break-words" data-testid="company-name">
          {company.name}
        </p>
        {address ? (
          <p className="break-words text-[var(--color-muted-foreground)]" data-testid="company-address">
            {address.line1}
            {address.line2 ? `, ${address.line2}` : ''}
            <br />
            {t('company.cityLine', {
              city: address.city,
              state: address.state,
              postalCode: address.postalCode,
            })}
          </p>
        ) : null}
        {company.phone || company.licenseNumber ? (
          <p className="text-[var(--color-muted-foreground)]">
            {company.phone ? <span data-testid="company-phone">{company.phone}</span> : null}
            {company.phone && company.licenseNumber ? <span aria-hidden="true"> · </span> : null}
            {company.licenseNumber ? (
              <span data-testid="company-license">
                {t('company.license', { number: company.licenseNumber })}
              </span>
            ) : null}
          </p>
        ) : null}
      </div>
    </div>
  );
}
